import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { Result, Button } from 'antd'
import { useTranslation } from 'react-i18next'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

function ErrorFallback({ error, onReset }: { error?: Error; onReset: () => void }) {
  const { t } = useTranslation('common')
  return (
    <Result
      status="500"
      title={t('error.title')}
      subTitle={error?.message || t('error.subtitle')}
      extra={
        <Button type="primary" onClick={onReset}>
          {t('error.tryAgain')}
        </Button>
      }
    />
  )
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" aria-live="polite">
          <ErrorFallback error={this.state.error} onReset={this.handleReset} />
        </div>
      )
    }

    return this.props.children
  }
}
